import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FileText, PlayCircle, ExternalLink, Sparkles, CheckCircle2, Loader2, Trophy, Check, X } from "lucide-react";
import { CharacterById } from "@/components/LanguageCharacters";
import { useT } from "@/lib/i18n";

function getEmbedUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

export default function LanguageMaterial() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [phase, setPhase] = useState("view"); // view | quiz | result
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const character = localStorage.getItem("lang_guide") || "owl";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/languages/materials/${id}`);
      setMaterial(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Material no encontrado");
      navigate("/idiomas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const completeAndStartQuiz = async () => {
    setCompleting(true);
    try {
      // mark complete
      const c = await api.post(`/languages/materials/${id}/complete`);
      if (c.data.points_awarded > 0) {
        toast.success(`+${c.data.points_awarded} puntos`);
      }
      // generate quiz
      setQuizLoading(true);
      const q = await api.post(`/languages/materials/${id}/quiz/generate`);
      setQuiz(q.data);
      setPhase("quiz");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando examen");
    } finally {
      setCompleting(false);
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    try {
      const r = await api.post(`/languages/materials/${id}/quiz/submit`, { answers });
      setResult(r.data);
      setPhase("result");
      if (r.data.xp_gained) toast.success(`+${r.data.xp_gained} XP · +${r.data.coins_gained} monedas`);
      if (r.data.global_points_awarded) toast.success(`+${r.data.global_points_awarded} pts globales`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando examen");
    }
  };

  if (loading) return <div className="text-[#374151]">{t("common.loading")}</div>;
  if (!material) return null;

  if (phase === "result" && result) {
    return <ResultView t={t} result={result} quiz={quiz} character={character} onBack={() => navigate(`/idiomas/${material.lang}`)} onRetry={() => { setPhase("quiz"); setAnswers({}); setResult(null); }} />;
  }

  if (phase === "quiz" && quiz) {
    return <QuizView t={t} quiz={quiz} answers={answers} setAnswers={setAnswers} onSubmit={submitQuiz} character={character} onCancel={() => setPhase("view")} />;
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="language-material-page">
      <button
        onClick={() => navigate(`/idiomas/${material.lang}`)}
        className="text-sm text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1 mb-4"
        data-testid="back-to-lang-map"
      >
        <ArrowLeft className="h-4 w-4" /> {t("mat.back_to_map")}
      </button>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 md:p-8 shadow-sm">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
          {t("mat.material")} · {material.resource_type.toUpperCase()} · {labelLang(material.lang, t)}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A] mt-1 mb-2">{material.title}</h1>
        {material.description && <p className="text-[#6B7280] mb-6">{material.description}</p>}

        <MaterialContent material={material} t={t} />

        {/* CTA */}
        <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
          {material.completed && !quizLoading ? (
            <div className="bg-[#EFF6FF] border border-[#1B2E7A]/30 rounded-xl p-4 mb-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#1B2E7A] mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1B2E7A]">{t("mat.completed_label")}</p>
                <p className="text-xs text-[#6B7280]">{t("mat.completed_hint")}</p>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={completeAndStartQuiz}
              disabled={completing || quizLoading}
              className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="start-quiz"
            >
              {quizLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("mat.generating")}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" /> {material.completed ? t("mat.start_quiz_done") : t("mat.start_quiz")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MaterialContent({ material, t }) {
  if (material.resource_type === "note") {
    return (
      <div className="bg-[#FFFFFF] border-l-4 border-[#F4B81F] rounded-r-xl p-5">
        <p className="text-sm md:text-base text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">{material.body}</p>
      </div>
    );
  }
  if (material.resource_type === "video") {
    const embed = getEmbedUrl(material.url);
    const fileSrc = material.url_type === "url" ? material.url : `${API}/files/${material.url}`;
    return (
      <div className="rounded-xl overflow-hidden border border-[#E5E7EB] aspect-video bg-black" data-testid="video-player">
        {embed ? (
          <iframe src={embed} title={material.title} allow="autoplay; encrypted-media; fullscreen" allowFullScreen className="w-full h-full" />
        ) : material.url_type === "upload" ? (
          <video controls src={fileSrc} className="w-full h-full" />
        ) : (
          <div className="flex items-center justify-center h-full text-white">
            <a href={material.url} target="_blank" rel="noreferrer" className="underline">Abrir video</a>
          </div>
        )}
      </div>
    );
  }
  if (material.resource_type === "pdf") {
    const fileSrc = material.url_type === "url" ? material.url : `${API}/files/${material.url}`;
    return (
      <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
        <iframe src={fileSrc} title={material.title} className="w-full" style={{ height: "70vh" }} />
        <a href={fileSrc} target="_blank" rel="noreferrer" className="block px-4 py-2 text-xs text-[#1B2E7A] hover:underline bg-[#FFFFFF]">
          <ExternalLink className="h-3 w-3 inline mr-1" /> {t("mat.open_external")}
        </a>
      </div>
    );
  }
  // link
  return (
    <a
      href={material.url}
      target="_blank"
      rel="noreferrer"
      className="block bg-[#FFFFFF] rounded-xl p-5 border border-[#E5E7EB] hover:border-[#1B2E7A] transition-all"
    >
      <p className="text-sm text-[#1B2E7A] inline-flex items-center gap-1 mb-1">
        <ExternalLink className="h-4 w-4" /> {t("mat.open_link")}
      </p>
      <p className="text-xs text-[#6B7280] truncate">{material.url}</p>
    </a>
  );
}

function QuizView({ t, quiz, answers, setAnswers, onSubmit, character, onCancel }) {
  const total = quiz.questions.length;
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === total;

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="material-quiz">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onCancel} className="text-[#6B7280] hover:text-[#1B2E7A]" data-testid="cancel-quiz">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4B81F] inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> {t("mat.quiz.eyebrow")}
          </p>
          <h1 className="font-heading text-2xl text-[#1A1A1A]">{quiz.title}</h1>
        </div>
        <CharacterById id={character} size={64} />
      </div>

      <div className="space-y-4">
        {quiz.questions.map((q, idx) => (
          <div key={q.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid={`q-${q.id}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
              {t("mat.quiz.q_label", { i: idx + 1, total })}
            </p>
            <h3 className="font-heading text-lg text-[#1A1A1A] mb-4">{q.text}</h3>
            <div className="space-y-2">
              {q.options.map((opt) => {
                const selected = answers[q.id] === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.id }))}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                      selected
                        ? "border-[#1B2E7A] bg-[#1B2E7A]/5 text-[#1B2E7A] font-medium"
                        : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
                    }`}
                    data-testid={`q-${q.id}-opt-${opt.id}`}
                  >
                    {opt.text}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Sticky footer */}
      <div className="fixed inset-x-0 bottom-0 bg-white/95 border-t border-[#E5E7EB] backdrop-blur-md p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <p className="text-xs text-[#6B7280]">{t("mat.quiz.answered_count", { answered: answeredCount, total })}</p>
          <button
            onClick={onSubmit}
            disabled={!allAnswered}
            className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] disabled:opacity-40"
            data-testid="submit-quiz"
          >
            {t("mat.quiz.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultView({ t, result, quiz, character, onBack, onRetry }) {
  const pct = result.accuracy;
  const pass = result.passed;
  return (
    <div className="max-w-2xl mx-auto" data-testid="quiz-result">
      <div className="bg-white rounded-3xl border border-[#E5E7EB] p-8 text-center shadow-sm">
        <div className="flex justify-center mb-4">
          <CharacterById id={character} size={140} mood={result.perfect ? "cheer" : pass ? "idle" : "sad"} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{t("mat.quiz.eyebrow")} · {quiz?.title}</p>
        <h1 className="font-heading text-4xl text-[#1A1A1A] mt-1 mb-3">
          {result.perfect ? t("mat.quiz.result.title.perfect") : pass ? t("mat.quiz.result.title.passed") : t("mat.quiz.result.title.failed")}
        </h1>

        <div className="grid grid-cols-3 gap-3 max-w-md mx-auto mb-5">
          <Stat label={t("lesson.results.correct")} value={`${result.correct}/${result.total}`} />
          <Stat label={t("lesson.results.accuracy")} value={`${result.accuracy}%`} />
          <Stat label={t("lesson.results.xp")} value={`+${result.xp_gained}`} accent="#F4B81F" />
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-[#6B7280] mb-6 flex-wrap">
          <Badge emoji="🪙" label={t("lesson.results.coins", { n: result.coins_gained })} />
          {result.global_points_awarded > 0 && <Badge emoji="⭐" label={t("lesson.results.global_pts", { n: result.global_points_awarded })} />}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={onBack} className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A]" data-testid="result-back">
            {t("lesson.results.back")}
          </button>
          <button onClick={onRetry} className="px-6 py-3 rounded-xl bg-white border border-[#E5E7EB] text-[#6B7280] hover:text-[#1B2E7A] hover:border-[#1B2E7A]" data-testid="result-retry">
            {t("lesson.results.retry")}
          </button>
        </div>

        <details className="text-left mt-6" data-testid="quiz-review">
          <summary className="cursor-pointer text-sm text-[#6B7280] hover:text-[#1B2E7A]">
            {t("mat.quiz.result.review", { n: result.details.length })}
          </summary>
          <ul className="mt-3 space-y-2">
            {result.details.map((d, i) => (
              <li
                key={d.question_id}
                className={`text-sm px-3 py-2 rounded-lg border ${
                  d.is_correct ? "bg-[#EFF6FF] border-[#1B2E7A]/20" : "bg-[#FFF0EC] border-[#DC2626]/20"
                }`}
              >
                <div className="flex items-start gap-2">
                  {d.is_correct ? (
                    <Check className="h-4 w-4 mt-0.5 text-[#1B2E7A]" />
                  ) : (
                    <X className="h-4 w-4 mt-0.5 text-[#DC2626]" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-[#1A1A1A]">{i + 1}. {d.question}</p>
                    {d.explanation && <p className="text-xs text-[#6B7280] mt-0.5 italic">{d.explanation}</p>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </details>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#FFFFFF] rounded-xl p-3 text-center">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{label}</p>
      <p className="font-heading text-xl" style={{ color: accent || "#1A1A1A" }}>{value}</p>
    </div>
  );
}

function Badge({ emoji, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F6] border border-[#E5E7EB] text-xs">
      <span>{emoji}</span> {label}
    </span>
  );
}

function labelLang(code, t) {
  if (t) return { en: t("onb.opt.lang.en").replace("🇺🇸 ", ""), fr: t("onb.opt.lang.fr").replace("🇫🇷 ", ""), es: t("onb.opt.lang.es").replace("🇪🇸 ", "") }[code] || code;
  return { en: "Inglés", fr: "Francés", es: "Español" }[code] || code;
}
